/*jslint node: true, browser: true*/

"use strict";

var $ = require("pli/jquery");
var ko = require("pli/knockout");
var pli = require("pli/forms");
var pliObservable = require("pli/forms-knockoutcontrols");

var createContactPercentageFactory = require("../document/contactPercentageViewModel");
var dataCache = require("../datacache");
var entityRegistry = require("../entity-registry");
var formattedBehaviour = require("../ko-behaviours-formatted");
var selectionForm = require("../common-selectionform");
var viewModelInit = require("../document/viewModelInitExtensions");

//-------------------------------------------
function selectRoyalties(royaltiesArray, link) {
    /// <summary>
    /// Show royalties selection form and either
    /// copy (if "link" is falsy) or link (if "link" is truthy)
    /// selected royalties to the specified array.
    /// </summary>
    selectionForm.open({
        getDetailsApi: "Geo-CopyLinkDocument-GetDetails",
        getDetailsDataBuilder: function (items) { // needed to adapt request for Geo-CopyLinkDocument-GetDetails
            var guidIndex = items[0].length - 1;
            return {
                documentType: "Royalty",
                actionType: (link ? "Link" : "Copy"),
                copyLinkFrom: ko.utils.arrayMap(items, function (item) {
                    return {
                        name: item[0],
                        guid: item[guidIndex]
                    };
                })
            };
        },
        multiSelect: true,
        onAcceptedContext: royaltiesArray,
        onAccepted: function (newRoyalties) {
            if (!link) { // if copying, reset all guid-s (so that save will create a new, copied content)
                newRoyalties = selectionForm.utils.resetGuids(newRoyalties);
            }
            selectionForm.utils.pushObjectItems({
                targetArray: royaltiesArray,
                records: newRoyalties,
                getIdentity: (!link ? null : function (royalty) { return royalty.guid(); })
            });
        },
        searchApi: "Geo-Document-SearchRoyalties",
        text: {
            acceptButton: (link ? "Link" : "Copy"),
            examples: [
                "<strong>File Numbers</strong>:<br>C-12 will find C-123, C-1234, etc.<br>M001 will find M001, M0019, etc.",
                "<strong>Areas</strong>:<br>river will find GHOST RIVER, TWO RIVERS, etc.",
                "<strong>Lands</strong>:<br>68 17 4 will find Twp 68-Rge 17-W4M<br>94 h 13 c will find 94-H-11-C<br>Note: Sections are not searched",
                "<strong>Combinations</strong>:<br>C, lloyd will find contracts (starting with C) in the Lloydminster area"
            ],
            iconClass: (link ? "sprite-link-16" : "sprite-copy-16"),
            title: (link ? "Link Royalties" : "Copy Royalties")
        }
    });
}



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Royalty class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//---------------------------------------
// Create new Royalty object, populating
// it with values from the specified
// record (if any).
function Royalty(factories) {
    if (window.console && window.console.group) { window.console.group("Royalty.ctor", factories); }

    this.comments = ko.observable("");
    this.convertible = ko.observable(false);
    this.convertibleAsOf = ko.observable("");
    this.convertibleTo = pliObservable(null, pli.config.editorPrecisionPercentFormat, "numeric");
    this.deductionAmount = pliObservable(null, pli.config.editorPrecisionPercentFormat, "numeric");
    this.deductionFor = ko.observable("");
    this.gasPriceBrackets = ko.observable("");
    this.guid = ko.observable(null);
    this.oilSlidingScaleFactor = pliObservable(null, null, "numeric");
    this.oilSlidingScaleMax = pliObservable(null, null, "numeric");
    this.oilSlidingScaleMin = pliObservable(null, null, "numeric");
    this.payees = factories.payee.createObservableArray();
    this.payors = factories.payor.createObservableArray();
    this.percentageModel = pliObservable(null, dataCache.getObservable("productPercentageModel"), "required", "picklist");
    this.percentageAll = pliObservable(null, pli.config.editorPrecisionPercentFormat, "numeric");
    this.percentageGas = pliObservable(null, pli.config.editorPrecisionPercentFormat, "numeric");
    this.percentageGasMinDollars = pliObservable(null, "C2", "numeric");
    this.percentageOil = pliObservable(null, pli.config.editorPrecisionPercentFormat, "numeric");
    this.productionPercentage = pliObservable(null, pli.config.editorPrecisionPercentFormat, "numeric", formattedBehaviour);
    this.takeInKind = ko.observable(false);
    this.type = this.id = pliObservable(null, entityRegistry.royaltyType.getPicklistBehaviourData(), "required", "picklist"); // type and id point to the same observable
    this.link = ko.observable(null);

    // non-serialized properties
    this.extras = function RoyaltyExtras() { return; };
    this.extras.percentageModelType = ko.computed(Royalty.prototype.computePercentageModelType, this);
    this.extras.calculation = ko.computed(Royalty.prototype.computeCalculation, this);
    this.extras.convertibility = ko.computed(Royalty.prototype.computeConvertibility, this);
    this.extras.errorsCountFlat = ko.computed(factories.computeErrorsCount, this);
    this.extras.errorsCountPayor = ko.computed(Royalty.prototype._computePayorErrorsCount, this);
    this.extras.errorsCountPayee = ko.computed(Royalty.prototype._computePayeeErrorsCount, this);
    this.extras.errorsCount = ko.computed(Royalty.prototype.computeErrorsCount, this);
    this.extras.fieldsetGap = ko.computed(Royalty.prototype.computeFieldsetGap, this);
    this.extras.globalIsReadOnly = ko.observable().subscribeTo("state.isReadOnly", true);
    this.extras.isReadOnly = ko.computed(this._computeReadOnlyFlag, this); // has to be after stateIsReady
    this.extras.dependencies = ko.observableArray();

    if (window.console && window.console.groupEnd) { window.console.groupEnd(); }
}


//---------------------------------------
Royalty.prototype.computeCalculation = function computeCalculation() {
    var v, a, result = "", percentageModelType = this.extras.percentageModelType();
    if (percentageModelType === 1) { // "all"
        a = this.percentageAll();
        result = "".concat(a || 0, "% of all products");
    } else if (percentageModelType === 2) { // "custom"
        v = this.percentageOil();
        if (v) {
            result += "".concat(v, "% of Oil");
        }
        v = this.percentageGas();
        if (v) {
            a = this.percentageGasMinDollars();
            result += (result ? ", " : "").concat(v, "% of Gas", a ? " (min. $" + a + ")" : "");
        }
        if (result) {
            return result;
        }
        v = this.oilSlidingScaleFactor();
        if (v) {
            result = "".concat("Oil S/S:1/", v);
        }
        v = this.oilSlidingScaleMin();
        if (v) {
            a = this.oilSlidingScaleMax();
            if (a) {
                result += (result ? "," : "Oil S/S:").concat("$", v, "-", a);
            }
        }

        result = result || "Custom (no parameters)";
    } else if (percentageModelType === 3) {
        result = "Crown Sliding Scale";
    } else {
        result = "";
    }
    return result;
};


//---------------------------------------
Royalty.prototype.computeConvertibility = function computeConvertibility() {
    if (this.convertible()) {
        var to = this.convertibleTo() || 0,
            asOf = this.convertibleAsOf() || "";

        return "".concat(to, "%", asOf ? ", as of " : "", asOf);
    }
    return "Non-Conv.";
};


//---------------------------------------
Royalty.prototype.computeErrorsCount = function computeRoyaltyErrorsCount() {
    return (this.extras.errorsCountFlat()
        + this.extras.errorsCountPayor()
        + this.extras.errorsCountPayee());
};


//---------------------------------------
Royalty.prototype._computePayorErrorsCount = function computePayorErrorsCount() {
    var totalError = (this.payors().length > 0 && this.payors.totalPercentage.invalid()) ? 1 : 0;
    return this.payors.errorsCount() + totalError;
};


//---------------------------------------
Royalty.prototype._computePayeeErrorsCount = function computePayeeErrorsCount() {
    var totalError = (this.payees().length > 0 && this.payees.totalPercentage.invalid()) ? 1 : 0;
    return this.payees.errorsCount() + totalError;
};


//---------------------------------------
Royalty.prototype.computeErrorsCountFlat = viewModelInit.computeErrorsCount;
Royalty.prototype.itemComparer = null; // disable client-side sorting


//---------------------------------------
// compute brackets field height based on properties
// that affect field visibility to keep payor
// and payee grids vertically aligned
Royalty.prototype.computeFieldsetGap = function computeFieldsetGap() {
    if (this.convertible()) {
        switch (this.extras.percentageModelType()) {
        case 1: // "all"
            return 91;
        case 2: // "custom"
            return 0;
        default:
            return 117;
        }
    }
    switch (this.extras.percentageModelType()) {
    case 1: // "all"
        return 39;
    case 2: // "custom"
        return 0;
    default:
        return 65;
    }
};


//---------------------------------------
// convert string in percentageModel property
// (case-insensitively) into an integer that
// we can use to bind visibility attributes
Royalty.prototype.computePercentageModelType = function computePercentageModelType() {
    var model = this.percentageModel();
    if (model) {
        model = $.trim(model.toLowerCase());
        if (model === "all") {
            return 1;
        }
        if (model === "custom") {
            return 2;
        }
        if (model === "css") {
            return 3;
        }
    }
    return 0;
};


//---------------------------------------
Royalty.prototype.initGridArray = function initRoyaltyGridArray(gridArray) {
    /// <summary>
    /// Add extra methods to the specified
    /// royalties grid array (that has just
    /// been created).
    /// </summary>
    gridArray.copyItems = gridArray.factory.factories.secureAction(Royalty.prototype._copyItems, gridArray);
    gridArray.linkItems = gridArray.factory.factories.secureAction(Royalty.prototype._linkItems, gridArray);
    gridArray.deleteAndCloseCurrent = gridArray.factory.factories.secureAction(gridArray.deleteAndCloseCurrent, gridArray, gridArray.factory.factories.checkForDependencies.bind(gridArray));
    gridArray.openNew = gridArray.factory.factories.secureAction(gridArray.openNew, gridArray);
};


//---------------------------------------
Royalty.prototype.loadViewModel = function loadRoyalty(record) {
    if (window.console && window.console.group) { window.console.group("Royalty.loadViewModel", record); }

    if (!record) { record = {}; }

    this.comments(record.comments || "");
    this.convertible(!!record.convertible);
    this.convertibleAsOf(record.convertibleAsOf || "");
    this.convertibleTo.load(record.convertibleTo);
    this.deductionAmount.load(record.deductionAmount);
    this.deductionFor(record.deductionFor || "");
    this.gasPriceBrackets(record.gasPriceBrackets || "");
    this.guid = ko.observable(record.guid || null);
    this.link(record.link || null);
    this.oilSlidingScaleFactor.load(record.oilSlidingScaleFactor);
    this.oilSlidingScaleMax.load(record.oilSlidingScaleMax);
    this.oilSlidingScaleMin.load(record.oilSlidingScaleMin);
    this.payees.loadViewModel(record.payees);
    this.payees.isReadOnly(!!this.link());
    this.payors.loadViewModel(record.payors);
    this.payors.isReadOnly(!!this.link());
    this.percentageModel.load(record.percentageModel);
    this.percentageAll.load(record.percentageAll);
    this.percentageGas.load(record.percentageGas);
    this.percentageGasMinDollars.load(record.percentageGasMinDollars);
    this.percentageOil.load(record.percentageOil);
    this.productionPercentage.load(record.productionPercentage);
    this.takeInKind(!!record.takeInKind);
    this.type.load(record.type || "");

    this.extras.dependencies(record.dependencies || []);

    if (window.console && window.console.groupEnd) { window.console.groupEnd(); }
};


//---------------------------------------
Royalty.prototype._computeReadOnlyFlag = function computeRoyaltyReadOnlyFlag() {
    var readonly = this.extras.globalIsReadOnly(), link = this.link();
    return readonly || link;
};


//---------------------------------------
Royalty.prototype._copyItems = function copyRoyalties() {
    /// <summary>
    /// Copy royalties from other documents. Shows
    /// a selection form that allows user to select
    /// and accept royalties to copy.
    /// </summary>
    /// <remarks>
    /// Expects "this" to be a grid observable
    /// array that creates and stores royalties.
    /// </remarks>
    selectRoyalties(this, false); // false => copy
};


//---------------------------------------
Royalty.prototype._linkItems = function linkRoyalties() {
    /// <summary>
    /// Link royalties from other documents. Shows
    /// a selection form that allows user to select
    /// and accept royalties to link.
    /// </summary>
    /// <remarks>
    /// Expects "this" to be a grid observable
    /// array that creates and stores royalties.
    /// </remarks>
    selectRoyalties(this, true); // true => link
};


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Export
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

module.exports = viewModelInit.standardFactoryCreatorFor(
    Royalty,
    "royalties",
    function createExtraRoyaltyFactories(rootId) {
        return {
            payor: createContactPercentageFactory(rootId, "payors", true), // true => client total percentage calculation
            payee: createContactPercentageFactory(rootId, "payees", true)  // true => client total percentage calculation
        };
    }
);



//////////////////
// WEBPACK FOOTER
// ./app/document-royalties/royaltyViewModel.js
// module id = 543
// module chunks = 49